import React, { useCallback, useEffect, useRef, useState } from 'react';

import Loader from 'src/shared/components/loader/Loader';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import { Access, Resources } from 'src/shared/types';
import NoData from 'src/shared/components/no-data/NoData';
import NodataImage from 'src/assets/svg/projectlist-nodata.svg?url';
import usePermissions from 'src/shared/hooks/usePermissions';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentCustomerId } from 'src/app/customers/state/customersSelector';
import { SecondaryButton } from 'src/shared/components/button/Button';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import type { SelectOption } from 'src/shared/components/select/Select';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Show from 'src/shared/components/show/Show';
import Pagination from 'src/shared/components/pagination/Pagination';
import ProjectSearchField from 'src/shared/components/project-search/ProjectSearchField';
import { useDebouncedListSearch } from 'src/shared/hooks/useDebouncedListSearch';
import Select from 'src/shared/components/select/Select';
import { resetPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import { clearAllGnrState } from 'src/app/portfolio/views/global-nature-risk/state/gnrFiltersSlice';
import styles from './ProjectList.module.scss';
import ProjectListItem from './components/project-list-item/ProjectListItem';
import { setCurrentProjectId } from './state/projectListSlice';
import { useProjectListQuery } from './state/api/projectListGraphSlice';
import type { ProjectCardData } from './state/api/queries/projectsListQuery';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export const ProjectsList = () => {
    const currentCustomerId = useAppSelector(getCurrentCustomerId) || '';
    const { allCustomers, currentCustomerId: selectedCustomerId, setCurrentCustomerId } = useCustomer();
    const [organizationSearchText, setOrganizationSearchText] = useState(
        allCustomers?.customers?.find(customer => customer.customerId === selectedCustomerId)?.customerName || ''
    );
    const {
        inputValue: siteSearchText,
        activeSearch,
        onInputChange: handleSiteSearchInputChange,
        reset: resetSiteSearch,
    } = useDebouncedListSearch();
    const [pageSize, setPageSize] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState(1);

    const { data: projectListData, isLoading: isLoadingProjects } = useProjectListQuery({
        customerId: currentCustomerId,
        pagination: { page: currentPage, pageSize },
        searchText: activeSearch,
    });

    const dispatch = useAppDispatch();
    const navigate = useAppNavigation();
    const { hasPermission, isCurrentUserInternal } = usePermissions();

    const canAdminSurveyDesign = hasPermission({
        to: [Access.CREATE, Access.UPDATE],
        customerId: currentCustomerId,
        resource: Resources.SURVEY_DESIGN,
    });

    useEffect(() => {
        dispatch(setCurrentProjectId(null));
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        resetSiteSearch();
    }, [currentCustomerId, resetSiteSearch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeSearch]);

    const handlePageSizeChange = useCallback((value: string) => {
        setPageSize(Number(value));
        setCurrentPage(1);
    }, []);

    // `data` from RTK Query keeps the last successful result during refetch; use it for totals so pagination stays visible.
    const projects = projectListData?.projects ?? [];
    const totalProjects = projectListData?.pagination?.total ?? 0;
    const lastPageNumber = Math.max(1, Math.ceil(totalProjects / pageSize));

    const pageSizeOptions: SelectOption[] = PAGE_SIZE_OPTIONS.map(size => ({
        value: String(size),
        label: String(size),
    }));

    const handleCustomerChange = (customerId: string | null) => {
        if (customerId) {
            if (customerId !== selectedCustomerId) {
                dispatch(resetPortfolioFilters());
                dispatch(clearAllGnrState());
            }

            setCurrentCustomerId(customerId);

            const selectedCustomer = allCustomers?.customers?.find(customer => customer.customerId === customerId);
            if (selectedCustomer?.hasPortfolio) {
                navigate.toPortfolioSummary(customerId);
            }
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const getCustomerName = useCallback(
        (customer: { customerName: string; customerCode: string }) => {
            if (isCurrentUserInternal) {
                return customer.customerName + ' (' + customer.customerCode + ')';
            }
            return customer.customerName;
        },
        [isCurrentUserInternal]
    );

    const selectedCustomerRow = allCustomers?.customers?.find(customer => customer.customerId === selectedCustomerId);
    const organisationLabelSyncKey = !selectedCustomerId
        ? ''
        : selectedCustomerRow
        ? `${selectedCustomerId}|${String(isCurrentUserInternal)}|${selectedCustomerRow.customerName}|${selectedCustomerRow.customerCode}`
        : `${selectedCustomerId}|pending`;

    const prevOrganisationLabelSyncKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (organisationLabelSyncKey === prevOrganisationLabelSyncKeyRef.current) {
            return;
        }
        prevOrganisationLabelSyncKeyRef.current = organisationLabelSyncKey;

        if (!selectedCustomerId) {
            setOrganizationSearchText('');
            return;
        }

        const row = allCustomers?.customers?.find(customer => customer.customerId === selectedCustomerId);
        if (row) {
            setOrganizationSearchText(getCustomerName(row));
        }
    }, [organisationLabelSyncKey, selectedCustomerId, allCustomers, getCustomerName]);

    const customerOptions: SelectOption[] =
        allCustomers?.customers?.map(customer => ({ value: customer.customerId, label: getCustomerName(customer) })) || [];

    const title = 'My sites';
    const subTitle = 'Select a site to explore nature performance and insights';

    const mainContent = (
        <div className={styles.wrapper}>
            <div className={styles.topSection}>
                <div className={styles.toolbarRow} data-testid='project-list-toolbar'>
                    {customerOptions.length > 1 && (
                        <div className={`${styles.filterField} ${styles.fixedWidthFilter}`} data-testid='organisation-filter'>
                            <label className={styles.filterLabel}>Organisation</label>
                            <Autocomplete
                                options={customerOptions}
                                value={selectedCustomerId || ''}
                                onChange={handleCustomerChange}
                                onInputChange={setOrganizationSearchText}
                                inputValue={organizationSearchText}
                                placeholder='Select organisation'
                                width='100%'
                                customStyle={{ borderRadius: '5px' }}
                                showAllOnOpen
                            />
                        </div>
                    )}
                    <ProjectSearchField
                        className={`${styles.filterField} ${styles.searchField} ${styles.fixedWidthFilter}`}
                        labelClassName={styles.filterLabel}
                        inputClassName='w-full min-w-0'
                        label='Search sites'
                        placeholder='Search by site name or id'
                        value={siteSearchText}
                        onChange={handleSiteSearchInputChange}
                        testId='search-sites-filter'
                        labelTestId='search-sites-label'
                    />
                    <div className={styles.filterField} data-testid='per-page-filter'>
                        <label className={styles.filterLabel}>Per page</label>
                        <Select
                            options={pageSizeOptions}
                            selectedValue={String(pageSize)}
                            onChange={handlePageSizeChange}
                            width={80}
                            testId='page-size-select'
                        />
                    </div>
                    {totalProjects > pageSize && (
                        <div className={styles.paginationSummaryAndControls} data-testid='pagination-summary-and-controls'>
                            <span className={styles.paginationInfo}>
                                Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalProjects)} of{' '}
                                {totalProjects} sites
                            </span>
                            <Pagination
                                className={styles.paginationInToolbar}
                                currentPageNumber={currentPage}
                                lastPageNumber={lastPageNumber}
                                onChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Show when={!isLoadingProjects} fallback={<Loader />}>
                {projects.length > 0 ? (
                    <div className={styles.projectCardsList}>
                        {projects.map((project: ProjectCardData) => (
                            <ProjectListItem project={project} key={project.projectId} />
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyMessagetext}>
                        <NoData
                            image={NodataImage}
                            text=''
                            subText={
                                activeSearch
                                    ? 'No sites match your search. Try a different search term.'
                                    : 'No sites have been created yet. Please speak to our Customer Success team if you are having an issue.'
                            }
                            height='40vh'
                        />
                    </div>
                )}
            </Show>
        </div>
    );

    const otherActions = (
        <div className={styles.otherActions}>
            {canAdminSurveyDesign && <SecondaryButton onClick={navigate.toSurveyAdmin}>Survey admin</SecondaryButton>}
        </div>
    );

    return <StandardAppLayout mainContent={mainContent} title={title} subTitle={subTitle} otherActions={otherActions} />;
};

export default ProjectsList;
